// =============================================================================
// domain/aiVerdict.ts — what the coach is told, and what it is never told.
// -----------------------------------------------------------------------------
// ── 🚨 THE VERDICT IS ABOUT ONE ACCOUNT, AND IT CANNOT BE ABOUT SEVERAL ──
//
// The old client's report is single-account throughout, so this question never
// arises there. This one has an "All accounts" option, and the coach's prompt
// reasons hard about prop-firm limits: *"burned 6.4% of the $50,000 combine
// balance"*, *"your already-thin $1,815 remaining cushion"*.
//
// Those sentences are only true of ONE account. Josh's book holds a $50k Blusky
// combine, a $25k Apex, a $25k Lucid — different sizes, different drawdown types,
// different daily loss limits. Handing the coach a merged P&L with one account's
// rules, or with none, produces confident prose about a cushion that does not
// exist. **So the card refuses to render on "All accounts" and says why.**
//
// ⚠ THIS IS THE OPPOSITE CHOICE FROM FIX IT / PROVE IT (D112), and deliberately.
// A rule is a habit and follows the trader between accounts. A drawdown limit
// does not.
//
// ── 🚨 EVERY FIELD IS OPTIONAL, AND ABSENT IS SAFE ──
//
// The Edge Function formats only the fields it is given and instructs the model:
// *"If a field is null/absent, don't reference it."* So sending a subset is
// honest — the verdict is simply drawn from less. **Sending a WRONG number is
// not**, and that is why nothing here is estimated, defaulted or back-filled.
//
// ⚠ Four inputs the old client sends are NOT sent, because this client cannot
// measure them yet: risk of ruin, E-ratio, the confluence ranking and the
// pattern × exit pairings. They are the four dimensions, and they are the next
// piece of this tab. Until then the coach reasons without them rather than about
// a fabricated version of them.
// =============================================================================

import { isControllableLoss, controllableLossReasons } from './dayReport';
import { mfeCapture } from './capture';
import { sequenceSplit } from './leakSequence';
import type { LeakTrade } from './leaks';

/** ≥3 trades, matching the old client. Below it the coach has nothing to read. */
export const AI_MIN_TRADES = 3;

export interface VerdictAccount {
  id: string;
  name: string;
  firm?: string | null;
  accountType?: string | null;
  accountSize?: number | null;
  startingBalance?: number | null;
  currentBalance?: number | null;
  rules?: Record<string, unknown> | null;
}

export type VerdictGate =
  | { ok: true }
  | { ok: false; why: 'pro' | 'no-account' | 'thin'; message: string };

/**
 * May the card ask at all?
 *
 * ⚠ CHECKED BEFORE THE CALL, not after. Every ask costs money and burns one of a
 * capped daily allowance, so a request that could only produce a weak verdict
 * must never be sent — the trader would pay for it out of an allowance they
 * cannot see.
 */
export function verdictGate(
  account: VerdictAccount | null, tradeCount: number, pro: boolean,
): VerdictGate {
  /**
   * 🚨 THE PRO CHECK IS FIRST, AND THE ORDER IS THE POINT (#76).
   *
   * This card ASKS BY ITSELF — no click. `useAiVerdict` fires from an effect the
   * moment the key is non-null, so on the old client the equivalent live call is
   * fenced by `if (pro && H.n >= 3)` (app/index.html:42315) rather than by a
   * button guard. There is no click to intercept here, so the gate has to be the
   * thing that decides whether a key exists at all.
   *
   * ⚠ AND IT MUST OUTRANK THE OTHER TWO. Told "pick one account above", a free
   * user picks one — and is then told they need more trades, and then gets an
   * error. Three screens of instructions for a thing they cannot have. The first
   * answer has to be the true one.
   */
  if (!pro) {
    return {
      ok: false,
      why: 'pro',
      message: 'The AI Coach is a Pro feature. Pro reads your own trades — names '
        + 'the leak, writes the rule that closes it, and proves whether it moved '
        + 'your numbers.',
    };
  }
  if (!account) {
    return {
      ok: false,
      why: 'no-account',
      message: 'Pick one account above. The coach reasons about your drawdown '
        + 'limit, your daily loss limit and what is left of your cushion — and '
        + 'those are different numbers on every account, so a verdict about all '
        + 'of them at once would be about none of them.',
    };
  }
  if (tradeCount < AI_MIN_TRADES) {
    return {
      ok: false,
      why: 'thin',
      message: `Needs at least ${AI_MIN_TRADES} trades on this account in the `
        + `window. There ${tradeCount === 1 ? 'is' : 'are'} ${tradeCount}.`,
    };
  }
  return { ok: true };
}

const numOrNull = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && v !== null && v !== '' ? n : null;
};

const mean = (xs: readonly number[]): number | null =>
  (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

/**
 * The figures the coach is given, in the old client's field names.
 *
 * ⚠ THE NAMES MATCH `buildUserPrompt` IN THE EDGE FUNCTION EXACTLY. It formats
 * by key, so a renamed field is not a type error — it is a figure the model
 * silently never sees, and the verdict quietly gets worse with nothing on screen
 * to say so.
 */
export function buildVerdictPayload(
  account: VerdictAccount,
  periodLabel: string,
  trades: readonly LeakTrade[],
  controllableTags: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const wins = trades.filter((t) => (Number(t.netPnl) || 0) > 0).length;
  const losses = trades.filter((t) => (Number(t.netPnl) || 0) < 0).length;
  const decided = wins + losses;

  const realizedRs = trades
    .map((t) => numOrNull(t.rrRealized)).filter((r): r is number => r !== null);
  const plannedRs = trades
    .map((t) => numOrNull(t.rrPlanned)).filter((r): r is number => r !== null);

  const clHits = trades.filter((t) => isControllableLoss(t, controllableTags));
  const reasonCount = new Map<string, number>();
  for (const t of clHits) {
    for (const r of controllableLossReasons(t, controllableTags)) {
      reasonCount.set(r, (reasonCount.get(r) ?? 0) + 1);
    }
  }
  const topLossReasons = [...reasonCount.entries()]
    .sort((a, b) => b[1] - a[1]).slice(0, 3).map(([r]) => r);

  const { afterLoss } = sequenceSplit(trades);
  const afterLossWins = afterLoss.filter((t) => (Number(t.netPnl) || 0) > 0).length;
  const afterLossDecided = afterLoss
    .filter((t) => (Number(t.netPnl) || 0) !== 0).length;

  const cap = mfeCapture(trades);
  const rules = account.rules ?? {};

  const out: Record<string, unknown> = {
    account: account.firm ? `${account.name} (${account.firm})` : account.name,
    period: periodLabel,
    trades: trades.length,
    accountContext: {
      type: account.accountType ?? null,
      size: numOrNull(account.accountSize),
      startingBalance: numOrNull(account.startingBalance),
      currentBalance: numOrNull(account.currentBalance),
      maxDrawdown: numOrNull(rules['maxDDValue']),
      maxDrawdownType: rules['maxDDType'] ?? null,
      dailyLossLimit: numOrNull(rules['dailyLossLimit']),
      profitTarget: numOrNull(rules['profitTarget']),
      maxContracts: numOrNull(rules['maxContracts']),
    },
    netPnl: trades.reduce((s, t) => s + (Number(t.netPnl) || 0), 0),
    controllableLossCount: clHits.length,
    controllableLossTotal: clHits.reduce(
      (s, t) => s + Math.abs(Number(t.controllableLossAmount) || 0), 0,
    ),
  };

  // ⚠ EACH ONE OMITTED WHEN IT CANNOT BE MEASURED, never sent as 0. A zero win
  // rate and an unmeasurable one are different facts, and the model was told to
  // ground every claim in what it was given — so a fabricated zero becomes a
  // confident sentence about a losing streak that never happened.
  if (decided) out['winRatePct'] = (wins / decided) * 100;
  if (realizedRs.length) out['avgR'] = mean(realizedRs);
  if (realizedRs.length) out['realizedR'] = mean(realizedRs);
  if (plannedRs.length) out['plannedR'] = mean(plannedRs);
  if (cap.pct !== null) out['mfeCapturePct'] = cap.pct;
  if (topLossReasons.length) out['topLossReasons'] = topLossReasons;
  if (afterLossDecided) out['wrAfterLoss'] = (afterLossWins / afterLossDecided) * 100;

  return out;
}

// ---------------------------------------------------------------------------
// Rendering the model's emphasis
// ---------------------------------------------------------------------------

export interface VerdictSpan { text: string; bold: boolean }

/**
 * Split `**double-asterisk**` emphasis into spans.
 *
 * ⚠ SPANS, NOT HTML. The old client escapes the text and then injects `<b>` via
 * `innerHTML`; that is safe only for as long as the escape is right, and the
 * string is MODEL OUTPUT reaching a page that holds the trader's session. React
 * renders these as elements, so there is no markup path at all — the question of
 * escaping never arises.
 *
 * ⚠ An unclosed `**` stays literal rather than bolding the rest of the sentence.
 */
export function verdictSpans(s: string): VerdictSpan[] {
  const out: VerdictSpan[] = [];
  const re = /\*\*([\s\S]+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push({ text: s.slice(last, m.index), bold: false });
    out.push({ text: m[1]!, bold: true });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ text: s.slice(last), bold: false });
  return out.filter((x) => x.text !== '');
}
